import React, { useEffect, useState } from 'react'
import { useAtomValue } from 'jotai'
import { userAtom } from '../../store/userAtoms'
import { ActivityLogList } from './ActivityLogList'
import { TranslationKey, useTar } from '@/modules'
import { apis, runAssignmentStatusMigration } from '../../modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { HealthStatus, ServicesHealth } from './ServicesHealth'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { Button } from '@duck/ui'
import { toast } from 'sonner'

const healthStatusDisplayMap: Record<HealthStatus, TranslationKey> = {
  healthy: 'common.healthy',
  down: 'common.down',
  loading: 'common.loading',
}

// eb59fe-9c3328-17_09-02_23
const breakDepTag = (deploymentTag?: string) => {
  const [, , , day, month, hour, minute] = deploymentTag?.match(/^(\w+-\w+)-((\d\d)_(\d\d)-(\d\d)_(\d\d))$/) || []
  if (!deploymentTag || !day || !month || !hour || !minute) {
    return null
  }

  // Create UTC date since deployment tag now uses UTC time components
  const date = new Date(Date.UTC(new Date().getFullYear(), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute)))
  return date.toISOString()
}

export const AdminDashboard: React.FC = () => {
  const user = useAtomValue(userAtom)
  const { t } = useTar()

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState<UserService.AdminDashboardResponse | null>(null)
  const [dashboardLoading, setDashboardLoading] = useState(true)
  const [dashboardError, setDashboardError] = useState<string | null>(null)
  const [migrationLoading, setMigrationLoading] = useState(false)

  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true)
      setDashboardError(null)
      const response = await apis.userService.adminDashboardGet()
      setDashboardData(response.data)
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      setDashboardError(error instanceof Error ? error.message : 'Failed to fetch dashboard data')
    } finally {
      setDashboardLoading(false)
    }
  }


  useEffect(() => {
    // Fetch dashboard data immediately on mount
    fetchDashboardData()
  }, [])

  const handleRunAssignmentStatusMigration = async (trial: boolean) => {
    setMigrationLoading(true)
    try {
      const result = await runAssignmentStatusMigration({ trial })
      if (result.success) {
        toast.success(t('admin.migrationSuccess'), { description: result.message })
      } else {
        toast.error(t('admin.migrationError'), { description: result.message })
      }
    } catch (error) {
      toast.error(t('admin.migrationError'), {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      setMigrationLoading(false)
    }
  }

  const getCurrentDeploymentTag = () => {
    if (import.meta.env.VITE_NODE_ENV === 'development') {
      return 'local-dev'
    }

    return import.meta.env.VITE_DEPLOYMENT_TAG || 'unknown'
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
          {t('admin.dashboard')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('admin.dashboardDescription', { firstName: user?.firstName, lastName: user?.lastName })}
        </p>
      </div>


      {/* Admin Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card overflow-hidden shadow rounded-lg border border-border">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">U</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-muted-foreground truncate">{t('admin.totalUsers')}</dt>
                  <dd className="text-lg font-medium text-foreground">
                    {dashboardLoading ? '...' : dashboardData?.totalUsers ?? '-'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card overflow-hidden shadow rounded-lg border border-border">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">T</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-muted-foreground truncate">{t('admin.totalTasks')}</dt>
                  <dd className="text-lg font-medium text-foreground">
                    {dashboardLoading ? '...' : dashboardData?.totalTasks ?? '-'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card overflow-hidden shadow rounded-lg border border-border">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">C</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-muted-foreground truncate">{t('challenges.completedChallenges')}</dt>
                  <dd className="text-lg font-medium text-foreground">
                    {dashboardLoading ? '...' : dashboardData?.completedChallenges ?? '-'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card overflow-hidden shadow rounded-lg border border-border">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">F</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-muted-foreground truncate">{t('admin.frontendDeploymentTime')}</dt>
                  <dd className="text-lg font-medium text-indigo-600">
                    <RelativeDateTime isoDateString={breakDepTag(getCurrentDeploymentTag())} fallbackText="local-env" />
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Migrations */}
      {import.meta.env.VITE_NODE_ENV === 'development' && (
        <div className="bg-card shadow rounded-lg border border-border">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-foreground mb-2">
              {t('admin.migrations')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('admin.migrationsDescription')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="default"
                disabled={migrationLoading}
                onClick={() => handleRunAssignmentStatusMigration(false)}
              >
                {migrationLoading ? t('admin.migrationRunning') : t('admin.runAssignmentStatusMigration')}
              </Button>
              <Button
                variant="outline"
                disabled={migrationLoading}
                onClick={() => handleRunAssignmentStatusMigration(true)}
              >
                {t('admin.migrationDryRun')}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('admin.runAssignmentStatusMigrationDescription')}
            </p>
          </div>
        </div>
      )}

      {/* Service Health Breakdown - Staging/Local */}
      <ServicesHealth
        title={t('admin.serviceHealthStatus')}
        healthStatusDisplayMap={healthStatusDisplayMap}
        breakDeploymentTag={breakDepTag}
      />

      {/* Service Health Breakdown - Production (only show if not localhost) */}
      {/* {!isLocalhost && (
        <ServiceHealthCard
          services={prodServices}
          title={t('admin.serviceHealthStatus')}
          environment="prod"
        />
      )} */}

      {/* Recent Activity */}
      <div className="bg-card shadow rounded-lg border border-border">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-foreground mb-4">
            {t('admin.recentActivity')}
          </h3>
          {dashboardLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('common.loading')}...
            </div>
          ) : dashboardError ? (
            <div className="text-center py-8 text-error">
              {dashboardError}
            </div>
          ) : (
            <>
              {(dashboardData?.adminNewsLog && dashboardData.adminNewsLog.length > 0) && (
                <>
                  <h4 className="text-sm font-semibold text-foreground mb-2">Admin news</h4>
                  <ActivityLogList items={dashboardData.adminNewsLog} />
                </>
              )}
              {(dashboardData?.activityLog && dashboardData.activityLog.length > 0) && (
                <>
                  <h4 className="text-sm font-semibold text-foreground mt-6 mb-2">Activity</h4>
                  <ActivityLogList items={dashboardData.activityLog} />
                </>
              )}
              {(!dashboardData?.adminNewsLog || dashboardData.adminNewsLog.length === 0) &&
                (!dashboardData?.activityLog || dashboardData.activityLog.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('admin.noRecentActivity')}
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
